import { useGetAgendasLookupsQuery, useGetTableLookupsQuery } from 'store/services/flow/lookups';
import { useGetViewFormItemAttributeSourcesQuery } from 'store/services/flow/agendaLookups';
import { memo, useCallback, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Delete, DragIndicator } from '@mui/icons-material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { EMAIL_REGEX } from 'utils/regex';
import { Box, FormControlLabel, MenuItem, styled } from '@mui/material';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Switch } from 'components/mui/common/Switch';
import { Checkbox } from 'components/mui/common/Checkbox';
import { IconButton } from 'components/mui/common/IconButton';
import { Select } from 'components/mui/common/Select';
import { TextField } from 'components/mui/common/TextField';
import type { ColumnType, FormItemType } from '../../types';
import { useParams } from 'react-router-dom';

const comparisonsTypeWhichDoesNotNeedValue = ['IsEmpty', 'IsNotEmpty', 'None'];

const Wrapper = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
}));

interface Props {
  index: number;
  handleRemoveColumn: (id: string) => void;
  parentType: string;
  columnType: ColumnType['type'] | FormItemType;
  editable?: boolean;
  agendaId: string;
  id: string;
}

export const ViewControlRow = memo(({ index, handleRemoveColumn, parentType, columnType, editable = false, id }: Props) => {
  const { t } = useTranslation();
  const { agendaId = '', id: viewId = '' } = useParams();
  const registerName = `shownColumns[${index}]`;

  const { control, setValue } = useFormContext();

  const [
    columnId,
    name,
    type,
    comparisonType,
    isVariable,
    isEnable,
    isFiltering,
    displayInFilter,
    isEditable,
    showInToolbar,
    showOnlyExistingValues,
  ] = useWatch({
    control,
    name: [
      `${registerName}.columnId`,
      `${registerName}.name`,
      `${registerName}.type`,
      `${registerName}.comparisonType`,
      `${registerName}.isVariable`,
      `${registerName}.isEnable`,
      `${registerName}.isFiltering`,
      `${registerName}.displayInFilter`,
      `${registerName}.isEditable`,
      `${registerName}.showInToolbar`,
      `${registerName}.showOnlyExistingValues`,
    ],
  });

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: columnId,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const { data: { comparisons: agendaComparisons } = {} } = useGetAgendasLookupsQuery(
    {
      formItemId: columnId,
    },
    { skip: parentType !== 'Agenda' }
  );

  const { data: { comparisons: tableComparisons } = {} } = useGetTableLookupsQuery(
    {
      columnId: columnId,
    },
    { skip: parentType !== 'Table' }
  );

  const comparisonTypes = useMemo(() => {
    const types = agendaComparisons || tableComparisons;

    if (types && types.length > 0) {
      const filteredTypes = types.filter((value) => value !== 'IsBiggerBy' && value !== 'IsSmallerBy');
      return ['None', ...filteredTypes];
    }
    return ['None'];
  }, [agendaComparisons, tableComparisons]);

  useEffect(() => {
    if (isVariable) {
      setValue(`${registerName}.staticValue`, '');
    }
  }, [isVariable, registerName, setValue]);

  const getIsComparisonType = useCallback(
    (valueType?: string) => comparisonsTypeWhichDoesNotNeedValue.includes(valueType || comparisonType),
    [comparisonType]
  );

  const getComponentByDataType = useMemo(() => {
    const commonProps = {
      label: name,
      fullWidth: true,
      background: 'white' as const,
      placeholder: `${t('common.write')} ${name}`,
      disabled: isVariable || getIsComparisonType(),
    };

    const renderTextField = (fieldType: string, rules = {}) => (
      <Controller
        control={control}
        name={`${registerName}.staticValue`}
        rules={rules}
        render={({ field, fieldState: { error } }) => (
          <TextField {...field} {...commonProps} type={fieldType} error={Boolean(error)} helperText={error?.message} />
        )}
      />
    );

    const typeConfig: Record<string, () => JSX.Element> = {
      Number: () => renderTextField('number'),
      Email: () =>
        renderTextField('email', {
          pattern: {
            value: EMAIL_REGEX,
            message: t('common.invalidEmail'),
          },
        }),
      Text: () => renderTextField('text'),
    };

    return (typeConfig[type as keyof typeof typeConfig] || typeConfig.Text)();
  }, [control, getIsComparisonType, isVariable, name, registerName, t, type]);

  const isEditableDisabled = columnType === 'Computed';

  const { data: { formItems: viewFormItems = [] } = {} } = useGetViewFormItemAttributeSourcesQuery(
    {
      agendaId,
      viewId,
      formItemId: id,
    },
    {
      skip: type !== 'Records' || comparisonType === 'None',
    }
  );

  return (
    <Wrapper ref={setNodeRef} style={style} data-testid={`admin-flow-table-view-row-${id}`}>
      <IconButton {...attributes} {...listeners}>
        <DragIndicator />
      </IconButton>
      <Controller
        control={control}
        name={`${registerName}.isEnable`}
        defaultValue={isEnable}
        render={({ field }) => (
          <Switch
            {...field}
            onChange={(_, value) => field.onChange(value)}
            checked={isEnable}
            dataTestId={`admin-flow-table-view-row-${id}-enabled-switch`}
          />
        )}
      />
      <Box width="150px" mb={-1}>
        <Select
          value={comparisonType}
          background="white"
          displayEmpty
          fullWidth
          dataTestId={`admin-flow-table-view-row-${id}-comparison-select`}
          disabled={comparisonTypes.length === 1 /* Only None */}
          onChange={(e) => {
            const value = e.target.value;
            setValue(`${registerName}.comparisonType`, value);
            setValue(`${registerName}.staticValue`, '');
            if (getIsComparisonType(value as string)) {
              setValue(`${registerName}.isVariable`, false);
              setValue(`${registerName}.attributeFormItemId`, null);
            }
          }}>
          {(
            comparisonTypes?.map((item: string) => ({
              id: item,
              value: item,
              label: t(`view.table.${item}`),
            })) ?? [
              {
                id: 'None',
                value: 'None',
                label: t('view.table.None'),
              },
            ]
          ).map((column) => (
            <MenuItem key={column.id} value={column.value}>
              {column.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box width="300px" mb={2}>
        {getComponentByDataType}
      </Box>
      {parentType === 'Agenda' && (
        <>
          <FormControlLabel
            label={t('view.table.filtering')}
            control={
              <Controller
                control={control}
                name={`${registerName}.isFiltering`}
                disabled={comparisonTypes.length === 1 /* Only None */}
                render={({ field }) => (
                  <Checkbox {...field} checked={isFiltering} dataTestId={`admin-flow-table-view-row-${id}-filtering-checkbox`} />
                )}
              />
            }
          />
          <FormControlLabel
            label={t('flow.view.displayInParentWhenFiltering')}
            control={
              <Controller
                control={control}
                name={`${registerName}.displayInFilter`}
                render={({ field }) => (
                  <Checkbox {...field} checked={displayInFilter} dataTestId={`admin-flow-table-view-row-${id}-display-in-filter-checkbox`} />
                )}
              />
            }
          />
        </>
      )}
      <FormControlLabel
        label={t('view.table.variable')}
        control={
          <Controller
            control={control}
            name={`${registerName}.isVariable`}
            disabled={getIsComparisonType()}
            render={({ field }) => <Checkbox {...field} checked={isVariable} dataTestId={`admin-flow-table-view-row-${id}-variable-checkbox`} />}
          />
        }
      />
      {type === 'Tables' && isFiltering && (
        <FormControlLabel
          label={t('flow.view.showOnlyExistingValues')}
          control={
            <Controller
              control={control}
              name={`${registerName}.showOnlyExistingValues`}
              disabled={!isFiltering}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={showOnlyExistingValues}
                  dataTestId={`admin-flow-table-view-row-${id}-show-only-existing-values-checkbox`}
                />
              )}
            />
          }
        />
      )}
      {comparisonType && comparisonType !== 'None' && type === 'Records' && (
        <Box width="150px">
          <Controller
            name={`${registerName}.attributeFormItemId`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                fullWidth
                error={Boolean(error)}
                helperText={error?.message}
                background="white"
                displayEmpty
                dataTestId={`admin-flow-table-view-row-${id}-attribute-select`}>
                {viewFormItems.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Box>
      )}
      {editable && (
        <FormControlLabel
          label={t('view.table.editable')}
          control={
            <Controller
              control={control}
              name={`${registerName}.isEditable`}
              render={({ field }) => (
                <Checkbox
                  {...field}
                  checked={isEditable}
                  disabled={isEditableDisabled}
                  dataTestId={`admin-flow-table-view-row-${id}-editable-checkbox`}
                />
              )}
            />
          }
        />
      )}
      {columnType === 'Button' && (
        <FormControlLabel
          label={t('flow.view.showInToolbar')}
          control={
            <Controller
              control={control}
              name={`${registerName}.showInToolbar`}
              render={({ field }) => (
                <Checkbox {...field} checked={showInToolbar} dataTestId={`admin-flow-table-view-row-${id}-show-in-toolbar-checkbox`} />
              )}
            />
          }
        />
      )}
      <IconButton onClick={() => handleRemoveColumn(columnId)} data-testid={`admin-flow-table-view-row-${id}-delete-button`}>
        <Delete />
      </IconButton>
    </Wrapper>
  );
});
